import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import Viewer from 'react-viewer';
import {
  Row, Col, Badge,
} from 'reactstrap';

import {
  downloadAllGuestAttachmentsRequest,
  downloadGuestAttachmentRequest,
  getGuestReferralRequest,
} from '@reducers/guest/GuestAction';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import ReferralDetails from '@shared/layout/dashboard/components/messages/components/referral-details/ReferralDetails';
import { LoadingBlock } from '@shared/utils';

import LinkStateScreen, { LINK_STATES, LinkState } from './components/LinkStateScreen';

import './GuestReferralStyle.scss';

const i18n = LocaleService.getTranslations('referrals.guest') as any;

/**
 * ENH-052 — the public, no-login referral view.
 *
 * Client (2026-09-23 Discord): "The guest view of the referral still needs to
 * show all the referral details including patient name, attachments (ability
 * to download attachment), exactly like what we show in the side bar when a
 * user clicks on a referral. They just can't respond to it." So it renders that
 * very sidebar — ReferralDetails — in its read-only guest mode, fed by the guest
 * endpoints. No app navigation, no messaging, no accept/decline: those are
 * account-only, and the API exposes no guest write endpoint at all.
 */
const GuestReferralPage = () => {
  const dispatch = useDispatch<any>();
  const { token } = useParams();
  // The token is stripped from the address bar once used (see below); keep it
  // for the file downloads this page makes afterwards.
  const tokenRef = useRef(token as string);
  const [ loading, setLoading ] = useState(true);
  const [ referral, setReferral ] = useState<any>(null);
  const [ linkState, setLinkState ] = useState<LinkState | null>(null);
  const [ images, setImages ] = useState<Array<{ src: string }>>([]);
  const [ showImage, setShowImage ] = useState(false);

  /**
   * The token travels in the URL, so suppress the Referer header for anything
   * this page triggers — otherwise a third party could receive a working
   * referral link in their logs. Scoped to this page and removed on unmount so
   * the rest of the app keeps its default referrer behaviour.
   */
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await dispatch(getGuestReferralRequest(token as string)).$promise;
        setReferral(result?.data?.data ?? null);

        // Security: the token sits in the URL, so strip it once it has been
        // used. Keeps it out of the address bar, browser history, and any
        // Referer header if the visitor later follows a link away.
        window.history.replaceState({}, '', '/referral/view');
      } catch (error: any) {
        const code = error?.response?.data?.error_code;
        setLinkState(LINK_STATES.includes(code) ? code : 'link_invalid');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      load();
    } else {
      setLinkState('link_invalid');
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ token ]);

  const downloadAttachment = async (attachment: any) => {
    const result = await dispatch(
      downloadGuestAttachmentRequest(tokenRef.current, attachment?.id),
    ).$promise;
    return result?.data?.data?.url;
  };

  const listAllAttachments = async () => {
    const result = await dispatch(downloadAllGuestAttachmentsRequest(tokenRef.current)).$promise;
    return result?.data?.data ?? [];
  };

  const onViewImage = (attachment: any) => {
    setImages([ { src: attachment?.file_url } ]);
    setShowImage(true);
  };

  // Prefills the sign-up form with the address this referral was sent to, the
  // same as the email's "Create Account" button.
  const onCreateAccount = () => {
    const email = referral?.referred_to?.email;
    window.location.href = email
      ? `/auth/register?email=${encodeURIComponent(email)}`
      : '/auth/register';
  };

  if (loading) {
    return <LoadingBlock withReforalLogo enabled />;
  }

  if (linkState) {
    return <LinkStateScreen state={linkState} token={token} />;
  }

  return (
    <div className="guest-referral-page">
      <div className="guest-referral-card">
        <div className="guest-referral-header">
          <Row className="align-items-center">
            <Col xs={8}>
              <div className="guest-referral-title">{i18n.title}</div>
            </Col>
            <Col xs={4} className="text-end">
              {referral?.is_urgent && (
                <Badge pill className="guest-referral-urgent-label">
                  {i18n.urgent}
                </Badge>
              )}
            </Col>
          </Row>
        </div>

        {/*
          The sidebar's styles are scoped to the messages page containers, so
          the details sit inside the same two; GuestReferralStyle.scss undoes
          only those containers' page-layout rules.
        */}
        <div className="referral-messages-container guest-referral-details">
          <div className="row-messages-style">
            <ReferralDetails
              loadingReferral={false}
              onViewImage={onViewImage}
              guest={{ referral, downloadAttachment, listAllAttachments }}
            />
          </div>
        </div>

        <div className="guest-referral-cta">
          <div className="guest-referral-cta-text mb-3">{i18n.ctaText}</div>
          <CustomButton
            id="guest-create-account-cta"
            className="btn-lg"
            color="primary"
            onClick={onCreateAccount}
            block
          >
            {i18n.createAccount}
          </CustomButton>
        </div>
      </div>
      <Viewer
        onClose={() => setShowImage(false)}
        visible={showImage}
        images={images}
        showTotal={false}
        zIndex={2000}
      />
    </div>
  );
};

export default GuestReferralPage;
